import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '../contexts/ThemeContext';
import {
  getIncidentCostBreakdown,
  getResponseMultiplier,
} from '../data/incidentCosts';

interface IncidentScore {
  incidentId: string;
  incidentTitle: string;
  responseScore: number;
  responseLabel: string;
}

interface ScenarioRiskCalculatorProps {
  scenarioTitle: string;
  incidentScores: IncidentScore[];
}

interface AggregatedCategory {
  name: string;
  totalBaseCost: number;
  totalAdjustedCost: number;
  incidentCount: number;
}

export const ScenarioRiskCalculator: React.FC<ScenarioRiskCalculatorProps> = ({
  scenarioTitle,
  incidentScores,
}) => {
  const { isLight } = useTheme();

  const calculatedData = useMemo(() => {
    const categoryTotals: Record<string, AggregatedCategory> = {
      detectionAndEscalation: { name: 'Detection & Escalation', totalBaseCost: 0, totalAdjustedCost: 0, incidentCount: 0 },
      legal: { name: 'Legal Counsel', totalBaseCost: 0, totalAdjustedCost: 0, incidentCount: 0 },
      notification: { name: 'Notification', totalBaseCost: 0, totalAdjustedCost: 0, incidentCount: 0 },
      customerExperience: { name: 'Customer Experience', totalBaseCost: 0, totalAdjustedCost: 0, incidentCount: 0 },
      postBreachResponse: { name: 'Post-Breach Response', totalBaseCost: 0, totalAdjustedCost: 0, incidentCount: 0 },
      directLoss: { name: 'Direct Financial Loss', totalBaseCost: 0, totalAdjustedCost: 0, incidentCount: 0 },
      lostRevenue: { name: 'Lost Revenue', totalBaseCost: 0, totalAdjustedCost: 0, incidentCount: 0 },
      liabilityAndFines: { name: 'Liability & Fines', totalBaseCost: 0, totalAdjustedCost: 0, incidentCount: 0 },
      brandDamage: { name: 'Brand & Reputation', totalBaseCost: 0, totalAdjustedCost: 0, incidentCount: 0 },
    };

    // Track which shared cost categories have been charged at full rate
    // Shared costs: first incident pays 100%, subsequent incidents pay 25%
    const sharedCostCategories = new Set(['detectionAndEscalation', 'legal', 'notification', 'postBreachResponse']);
    const chargedSharedCategories = new Set<string>();

    const incidentDetails: Array<{
      title: string;
      baseCost: number;
      adjustedCost: number;
      multiplier: number;
      multiplierLabel: string;
    }> = [];

    let totalBaseCost = 0;
    let totalAdjustedCost = 0;
    let totalMultiplierSum = 0;
    let incidentsWithCosts = 0;

    incidentScores.forEach((incident) => {
      const breakdown = getIncidentCostBreakdown(incident.incidentId);
      if (!breakdown) return;

      const { multiplier, label } = getResponseMultiplier(incident.responseScore);
      incidentsWithCosts++;
      totalMultiplierSum += multiplier;

      let incidentBaseCost = 0;
      let incidentAdjustedCost = 0;

      // Aggregate each category with shared cost logic
      const categories = breakdown.categories;
      Object.entries(categories).forEach(([key, cat]) => {
        if (cat.isApplicable && categoryTotals[key]) {
          // Determine cost multiplier based on shared cost model
          let costFactor = 1.0;
          if (sharedCostCategories.has(key)) {
            if (chargedSharedCategories.has(key)) {
              // Subsequent incidents pay 25% for shared IR infrastructure
              costFactor = 0.25;
            } else {
              // First incident pays full cost
              chargedSharedCategories.add(key);
            }
          }

          const effectiveBaseCost = Math.round(cat.baseCost * costFactor);
          categoryTotals[key].totalBaseCost += effectiveBaseCost;
          const adjusted = Math.round(effectiveBaseCost * multiplier);
          categoryTotals[key].totalAdjustedCost += adjusted;
          categoryTotals[key].incidentCount++;
          incidentBaseCost += effectiveBaseCost;
          incidentAdjustedCost += adjusted;
        }
      });

      totalBaseCost += incidentBaseCost;
      totalAdjustedCost += incidentAdjustedCost;

      incidentDetails.push({
        title: incident.incidentTitle || breakdown.incidentTitle,
        baseCost: incidentBaseCost,
        adjustedCost: incidentAdjustedCost,
        multiplier,
        multiplierLabel: label,
      });
    });

    const averageMultiplier = incidentsWithCosts > 0
      ? totalMultiplierSum / incidentsWithCosts
      : 1;

    return {
      categoryTotals,
      incidentDetails,
      totalBaseCost,
      totalAdjustedCost,
      averageMultiplier,
      incidentsWithCosts,
    };
  }, [incidentScores]);

  const formatCurrency = (amount: number) => {
    if (amount >= 1000000) {
      return `$${(amount / 1000000).toFixed(2)}M`;
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  if (calculatedData.incidentsWithCosts === 0) {
    return null;
  }

  const primaryCategories = ['detectionAndEscalation', 'legal', 'notification', 'customerExperience', 'postBreachResponse', 'directLoss'];
  const secondaryCategories = ['lostRevenue', 'liabilityAndFines', 'brandDamage'];

  return (
    <motion.div
      className={`rounded-2xl overflow-hidden ${
        isLight
          ? 'bg-white shadow-xl border border-slate-200'
          : 'bg-white/[0.03] border border-white/10 backdrop-blur-md'
      }`}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      {/* Header */}
      <div
        className={`px-6 py-4 border-b ${
          isLight ? 'bg-slate-50 border-slate-200' : 'bg-white/5 border-white/10'
        }`}
      >
        <h3 className={`text-xl font-semibold ${isLight ? 'text-slate-800' : 'text-white'}`}>
          Estimated Risk Quantification
        </h3>
        <p className={`text-sm mt-1 ${isLight ? 'text-slate-600' : 'text-slate-400'}`}>
          {scenarioTitle}
        </p>
      </div>

      {/* Cost Category Breakdown */}
      <div className="px-6 py-4">
        <table className="w-full">
          <tbody>
            {/* Primary Costs Header */}
            <tr>
              <td colSpan={2} className={`pb-1 text-xs font-semibold uppercase tracking-wider ${isLight ? 'text-slate-600' : 'text-slate-400'}`}>
                Primary Costs
              </td>
            </tr>
            {primaryCategories.map((key) => {
              const cat = calculatedData.categoryTotals[key];
              if (cat.totalAdjustedCost === 0) return null;
              return (
                <tr key={key} className={`border-t ${isLight ? 'border-slate-100' : 'border-white/5'}`}>
                  <td className={`py-2 ${isLight ? 'text-slate-700' : 'text-slate-300'}`}>
                    {cat.name}
                  </td>
                  <td className={`py-2 text-right font-mono ${isLight ? 'text-slate-800' : 'text-white'}`}>
                    {formatCurrency(cat.totalAdjustedCost)}
                  </td>
                </tr>
              );
            })}

            {/* Secondary Costs Header */}
            <tr>
              <td colSpan={2} className={`pt-4 pb-1 text-xs font-semibold uppercase tracking-wider ${isLight ? 'text-slate-600' : 'text-slate-400'}`}>
                Secondary Impact
              </td>
            </tr>
            {secondaryCategories.map((key) => {
              const cat = calculatedData.categoryTotals[key];
              if (cat.totalAdjustedCost === 0) return null;
              return (
                <tr key={key} className={`border-t ${isLight ? 'border-slate-100' : 'border-white/5'}`}>
                  <td className={`py-2 ${isLight ? 'text-slate-700' : 'text-slate-300'}`}>
                    {cat.name}
                  </td>
                  <td className={`py-2 text-right font-mono ${isLight ? 'text-slate-800' : 'text-white'}`}>
                    {formatCurrency(cat.totalAdjustedCost)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Total */}
      <div
        className={`px-6 py-5 border-t ${
          isLight ? 'bg-slate-50 border-slate-200' : 'bg-white/5 border-white/10'
        }`}
      >
        <div className="flex items-center justify-between">
          <div>
            <span className={`text-lg font-semibold ${isLight ? 'text-slate-800' : 'text-white'}`}>
              Total Impact
            </span>
            <p className={`text-sm mt-1 ${isLight ? 'text-slate-500' : 'text-slate-400'}`}>
              Base cost: {formatCurrency(calculatedData.totalBaseCost)} × avg {calculatedData.averageMultiplier.toFixed(2)} response modifier
            </p>
          </div>
          <span className={`text-3xl font-bold font-mono ${isLight ? 'text-slate-900' : 'text-white'}`}>
            {formatCurrency(calculatedData.totalAdjustedCost)}
          </span>
        </div>
      </div>
    </motion.div>
  );
};

export default ScenarioRiskCalculator;
